// src/components/InternalSentimentView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import DetailedListView from './DetailedListView';
import { ROUTES } from '../constants/routes';
import { formatDate, formatDateTime } from '../utils';
import CallEvalDisplay from './CallEvalDisplay';
import { DepartmentSelector } from './FilterControls';
import SmartFilterBar from './SmartFilterBar';
import { useEntityNavigationContext } from './EntityNavigation';
import { useStaffContext } from '../contexts/StaffContext';

const communicationColumns = [
    { header: 'Date', key: 'date', formatter: formatDateTime},
    { header: 'Type', key: 'type'},
    { header: 'Title', key: 'title'},
    { header: 'Staff', key: 'staff'},
    { header: 'Call Scores', key: 'callEvaluations', width: '50%', formatter: (evals) => (evals && evals.length > 0) ? <CallEvalDisplay evaluations={evals} /> : <span className="text-gray-400 italic text-xs">No Evals</span> },
    { header: 'Summary', key: 'summary', type: 'longText'},
    { header: 'Evidenced Entities', key: 'evidencedEntities', formatter: (val) => val?.map(e => `${e.name} (${e.type})`).join(', ') },
];

const InternalSentimentView = () => {
    const { apiService, departmentId: departmentIdFromContext, dateRange, setDateRange } = useOutletContext();
    const navigate = useNavigate();
    const { getDriDisplayName } = useStaffContext();
    const params = useParams();
    
    // Get filter expansion state from EntityNavigation context
    const { filtersExpanded } = useEntityNavigationContext();
    
    // Filters are always collapsible (controlled by EntityNavigation header)
    const filterVariant = 'collapsible';
    
    const activeRouteDepartmentId = departmentIdFromContext || params.departmentId;
    
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(activeRouteDepartmentId || '');
    const [loadingDepartments, setLoadingDepartments] = useState(true);
    
    const [communicationsList, setCommunicationsList] = useState([]);
    const [loadingComms, setLoadingComms] = useState(false);
    const [errorComms, setErrorComms] = useState(null);
    const [sentimentTrend, setSentimentTrend] = useState([]);
    const [loadingTrend, setLoadingTrend] = useState(false);

    // Load departments
    const loadDepartments = useCallback(async () => {
        if (!apiService) return;
        setLoadingDepartments(true);
        try {
            const deptResult = await apiService.getDepartmentList();
            const deptList = deptResult.map(r => r.department).filter(d => d);
            setDepartments(deptList);
            
            if (!selectedDepartment && activeRouteDepartmentId && deptList.includes(activeRouteDepartmentId)) {
                setSelectedDepartment(activeRouteDepartmentId);
            }
        } catch (err) {
            console.error('Error loading departments:', err);
        } finally {
            setLoadingDepartments(false);
        }
    }, [apiService, selectedDepartment, activeRouteDepartmentId]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    useEffect(() => {
        if (activeRouteDepartmentId && activeRouteDepartmentId !== selectedDepartment) {
            setSelectedDepartment(activeRouteDepartmentId);
        }
    }, [activeRouteDepartmentId, selectedDepartment]);

    const handleDepartmentChange = (newDept) => {
        navigate(`${ROUTES.OPERATIONS}/${newDept}/sentiment`);
    };

    // DRI state (for consistency with other views, even if not filtering yet)
    const [dris, setDris] = useState([]);
    const [selectedDris, setSelectedDris] = useState([]);

    const loadDris = useCallback(async () => {
        if (!apiService || !selectedDepartment) return;
        try {
            const driResult = await apiService.getDepartmentDRIs(selectedDepartment);
            setDris(driResult.map(r => r.dri));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        }
    }, [apiService, selectedDepartment]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDris();
        }
    }, [selectedDepartment, loadDris]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const hasActiveFilters = selectedDris.length > 0;
    
    const handleClearAllFilters = () => {
        setSelectedDris([]);
    };

    const loadDepartmentComms = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoadingComms(true); setErrorComms(null);
        try {
            // Get department communications with DRI filter
            const comms = await apiService.getDepartmentCommunications(
                selectedDepartment, 
                dateRange.start, 
                dateRange.end,
                selectedDris.length > 0 ? selectedDris : null
            );
            setCommunicationsList(comms);
        } catch (e) { setErrorComms(e.message); setCommunicationsList([]); }
        finally { setLoadingComms(false); }
    }, [apiService, selectedDepartment, dateRange, selectedDris]);

    const loadDepartmentSentimentTrend = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        setLoadingTrend(true);
        try {
            const trendData = await apiService.getDepartmentSentimentOverTime(selectedDepartment, dateRange.start, dateRange.end);
            setSentimentTrend(trendData?.map(item => ({
                ...item,
                weekBeginning: formatDate(item.weekBeginning) 
          })) ?? []);
        } catch (err) { console.error("Error loading dept sentiment trend:", err); setSentimentTrend([]); }
        finally { setLoadingTrend(false); }
    }, [apiService, selectedDepartment, dateRange]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDepartmentComms();
            loadDepartmentSentimentTrend();
        }
    }, [selectedDepartment, loadDepartmentComms, loadDepartmentSentimentTrend, selectedDris]);

    return (
        <>
            {/* SmartFilterBar for entity navigation */}
            <SmartFilterBar
                dateRange={dateRange} onDateRangeChange={setDateRange}
                dris={dris} selectedDris={selectedDris} onDriChange={handleDriChange} driDisplayFormatter={getDriDisplayName}
                filterGroups={[{ id: 'context', label: 'Context', filters: [{ key: 'department' }] }]}
                filters={{ department: selectedDepartment }}
                filterConfigs={{ department: { label: 'Department', render: () => departments.length > 0 && <DepartmentSelector departments={departments} selectedDepartment={selectedDepartment} onDepartmentChange={handleDepartmentChange} loading={loadingDepartments} /> } }}
                activeFilterChips={[]}
                onClearAll={handleClearAllFilters} hasActiveFilters={hasActiveFilters}
                variant={filterVariant} isExpanded={filtersExpanded}
            />
            
            <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6 md:mb-8">
                 <h3 className="text-lg font-medium text-gray-900 mb-4">Department Sentiment Trend (Weekly)</h3>
                 {loadingTrend ? <p>Loading chart...</p> : sentimentTrend.length > 0 ? (
                     <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={sentimentTrend} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="weekBeginning" tick={{fontSize: 10}} />
                            <YAxis domain={[0, 1]} tickFormatter={(value) => `${(value * 100).toFixed(0)}%`} tick={{fontSize: 10}} />
                            <RechartsTooltip wrapperClassName="text-xs" formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Avg Sentiment']} labelFormatter={(value) => `Week: ${value}`} />
                            <Line type="monotone" dataKey="avgSentiment" stroke="#3B82F6" strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} name="Avg Sentiment" />
                        </LineChart>
                      </ResponsiveContainer>
                  ) : (
                      <p className="text-center text-sm text-gray-500 py-4">No sentiment data available for this department.</p>
                  )}
             </div>

            <DetailedListView
                title="Department Communications"
                items={communicationsList}
                columns={communicationColumns}
                isLoading={loadingComms}
                error={errorComms}
            />
        </>
    );
};

export default InternalSentimentView;